import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Slider } from '../ui/slider';
import { Trophy, TrendingUp, Brain, Video, Search, Sparkles, Loader2, Medal, Star } from 'lucide-react';
import { combinedRankingsApi } from '../../lib/api';
import { toast } from 'sonner';

const CombinedRankingsTab = ({ sports = [] }) => {
    const [rankings, setRankings] = useState([]);
    const [schoolRankings, setSchoolRankings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState('athletes'); // athletes | schools
    const [filters, setFilters] = useState({
        sport: 'all',
        age_group: 'all',
        country: 'all',
        search: ''
    });
    const [weights, setWeights] = useState({ normal: 45, ai: 35, video: 20 });
    const [totalCount, setTotalCount] = useState(0);

    const ageGroups = ['U10', 'U12', 'U15', 'U18', 'U23', 'Open'];
    const countries = ['Kenya', 'Uganda', 'Tanzania', 'Rwanda', 'Ethiopia', 'Ghana', 'Nigeria', 'South Africa'];

    useEffect(() => {
        loadRankings();
    }, [filters.sport, filters.age_group, filters.country, weights, view]);

    const loadRankings = async () => {
        setLoading(true);
        try {
            const params = {
                limit: 100,
                weight_normal: weights.normal / 100,
                weight_ai: weights.ai / 100,
                weight_video: weights.video / 100,
            };
            if (filters.sport !== 'all') params.sport = filters.sport;
            if (filters.age_group !== 'all') params.age_group = filters.age_group;
            if (filters.country !== 'all') params.country = filters.country;

            if (view === 'athletes') {
                const res = await combinedRankingsApi.getAthletes(params);
                setRankings(res.data.rankings || []);
                setTotalCount(res.data.total || 0);
            } else {
                const res = await combinedRankingsApi.getSchools({ ...params, limit: 50 });
                setSchoolRankings(res.data.rankings || []);
                setTotalCount(res.data.total || 0);
            }
        } catch (e) {
            toast.error('Failed to load combined rankings');
        } finally {
            setLoading(false);
        }
    };

    const filteredRankings = rankings.filter(r => {
        if (!filters.search) return true;
        const q = filters.search.toLowerCase();
        return (r.athlete_name || '').toLowerCase().includes(q) ||
               (r.country || '').toLowerCase().includes(q) ||
               (r.school || '').toLowerCase().includes(q);
    });

    const filteredSchools = schoolRankings.filter(r => {
        if (!filters.search) return true;
        const q = filters.search.toLowerCase();
        return (r.school_name || '').toLowerCase().includes(q);
    });

    // Re-normalize weights to keep sum = 100
    const updateWeight = (key, val) => {
        const others = Object.keys(weights).filter(k => k !== key);
        const remaining = 100 - val;
        const otherSum = weights[others[0]] + weights[others[1]];
        if (otherSum === 0) {
            setWeights({ ...weights, [key]: val, [others[0]]: remaining / 2, [others[1]]: remaining / 2 });
        } else {
            setWeights({
                ...weights,
                [key]: val,
                [others[0]]: Math.round(weights[others[0]] / otherSum * remaining),
                [others[1]]: Math.round(weights[others[1]] / otherSum * remaining),
            });
        }
    };

    const presets = {
        balanced: { normal: 45, ai: 35, video: 20 },
        traditional: { normal: 70, ai: 20, video: 10 },
        ai_first: { normal: 25, ai: 50, video: 25 },
        scout_mode: { normal: 30, ai: 40, video: 30 },
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-500/10 via-cyan-500/10 to-purple-500/10 border border-purple-500/30 rounded-lg p-5">
                <div className="flex items-start gap-3">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                        <Sparkles className="w-5 h-5 text-purple-400" />
                    </div>
                    <div className="flex-1">
                        <h3 className="font-bold text-lg flex items-center gap-2">
                            Combined Rankings <span className="text-xs px-2 py-0.5 bg-purple-500/30 text-purple-200 rounded-full">NEW</span>
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                            Hybrid ranking that fuses Tournament Performance + AI Talent Score + Video Analysis. The classic Normal Ranking and AI Ranking continue unchanged in their own tabs.
                        </p>
                    </div>
                </div>
            </div>

            {/* Weight Configuration */}
            <Card className="bg-[#0d2847] border-white/10 p-5">
                <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-base flex items-center gap-2">
                        <Star className="w-4 h-4 text-yellow-400" />
                        Weight Configuration
                    </h4>
                    <div className="flex gap-2 flex-wrap">
                        {Object.entries(presets).map(([k, v]) => (
                            <Button key={k} size="sm" variant="outline" className="border-white/10 h-7 text-xs"
                                onClick={() => setWeights(v)}
                                data-testid={`combined-preset-${k}`}>
                                {k.replace('_', ' ')}
                            </Button>
                        ))}
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <WeightSlider label="Normal Ranking" icon={<Trophy className="w-4 h-4 text-yellow-400" />} value={weights.normal} onChange={(v) => updateWeight('normal', v)} description="Tournament wins, ELO, points" testId="weight-normal" />
                    <WeightSlider label="AI Talent Score" icon={<Brain className="w-4 h-4 text-purple-400" />} value={weights.ai} onChange={(v) => updateWeight('ai', v)} description="AI-derived potential & form" testId="weight-ai" />
                    <WeightSlider label="Video Analysis" icon={<Video className="w-4 h-4 text-cyan-400" />} value={weights.video} onChange={(v) => updateWeight('video', v)} description="Gemini Vision performance score" testId="weight-video" />
                </div>
            </Card>

            {/* View Toggle + Filters */}
            <div className="flex flex-wrap gap-3 p-4 bg-[#0d2847] border border-white/5 items-center rounded">
                <div className="flex bg-[#0a1628] rounded p-1 border border-white/10">
                    <button className={`px-3 py-1.5 text-sm rounded ${view === 'athletes' ? 'bg-primary text-white' : 'text-muted-foreground'}`}
                        onClick={() => setView('athletes')} data-testid="combined-view-athletes">
                        Athletes
                    </button>
                    <button className={`px-3 py-1.5 text-sm rounded ${view === 'schools' ? 'bg-primary text-white' : 'text-muted-foreground'}`}
                        onClick={() => setView('schools')} data-testid="combined-view-schools">
                        Schools
                    </button>
                </div>
                <div className="relative flex-1 min-w-[220px] max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Search..." value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                        className="pl-9 bg-[#0a1628] border-white/10 h-9"
                        data-testid="combined-search-input" />
                </div>
                <Select value={filters.sport} onValueChange={(v) => setFilters({ ...filters, sport: v })}>
                    <SelectTrigger className="w-[150px] bg-[#0a1628] border-white/10" data-testid="combined-sport-filter">
                        <SelectValue placeholder="Sport" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Sports</SelectItem>
                        {sports.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                </Select>
                {view === 'athletes' && (
                    <>
                        <Select value={filters.age_group} onValueChange={(v) => setFilters({ ...filters, age_group: v })}>
                            <SelectTrigger className="w-[120px] bg-[#0a1628] border-white/10" data-testid="combined-age-filter">
                                <SelectValue placeholder="Age" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Ages</SelectItem>
                                {ageGroups.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={filters.country} onValueChange={(v) => setFilters({ ...filters, country: v })}>
                            <SelectTrigger className="w-[150px] bg-[#0a1628] border-white/10" data-testid="combined-country-filter">
                                <SelectValue placeholder="Country" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Countries</SelectItem>
                                {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </>
                )}
                <span className="text-xs text-muted-foreground ml-auto">
                    {view === 'athletes' ? filteredRankings.length : filteredSchools.length} of {totalCount}
                </span>
            </div>

            {/* Results */}
            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
            ) : view === 'athletes' ? (
                <Card className="bg-[#0d2847] border-white/10 overflow-hidden">
                    <table className="w-full text-sm" data-testid="combined-rankings-table">
                        <thead className="bg-[#0a1628] border-b border-white/10">
                            <tr>
                                <th className="text-left px-4 py-3 font-semibold">#</th>
                                <th className="text-left px-4 py-3 font-semibold">Athlete</th>
                                <th className="text-left px-4 py-3 font-semibold">Sport</th>
                                <th className="text-left px-4 py-3 font-semibold">Tier</th>
                                <th className="text-right px-4 py-3 font-semibold">Combined</th>
                                <th className="text-right px-4 py-3 font-semibold">Normal</th>
                                <th className="text-right px-4 py-3 font-semibold">AI</th>
                                <th className="text-right px-4 py-3 font-semibold">Video</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRankings.map((r) => (
                                <tr key={r.athlete_id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                                    <td className="px-4 py-3 font-bold">
                                        {r.combined_rank <= 3 ? (
                                            <Medal className={`w-4 h-4 ${r.combined_rank === 1 ? 'text-yellow-400' : r.combined_rank === 2 ? 'text-gray-300' : 'text-amber-600'}`} />
                                        ) : `#${r.combined_rank}`}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="font-medium">{r.athlete_name}</div>
                                        <div className="text-xs text-muted-foreground">{r.country} {r.school ? `· ${r.school}` : ''}</div>
                                    </td>
                                    <td className="px-4 py-3 text-xs">{(r.sports || []).join(', ')}</td>
                                    <td className="px-4 py-3">
                                        <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded">
                                            {r.tier}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <span className="font-bold text-lg text-cyan-400">{r.combined_score}</span>
                                    </td>
                                    <td className="px-4 py-3 text-right text-yellow-400/80">{r.breakdown?.normal_score?.toFixed(1) || '0.0'}</td>
                                    <td className="px-4 py-3 text-right text-purple-400/80">{r.breakdown?.ai_talent_score || 0}</td>
                                    <td className="px-4 py-3 text-right text-cyan-400/80">{r.breakdown?.video_analysis_score || 0}</td>
                                </tr>
                            ))}
                            {filteredRankings.length === 0 && (
                                <tr><td colSpan={8} className="text-center py-12 text-muted-foreground">No athletes match the current filters</td></tr>
                            )}
                        </tbody>
                    </table>
                </Card>
            ) : (
                <Card className="bg-[#0d2847] border-white/10 overflow-hidden">
                    <table className="w-full text-sm" data-testid="combined-schools-table">
                        <thead className="bg-[#0a1628] border-b border-white/10">
                            <tr>
                                <th className="text-left px-4 py-3 font-semibold">#</th>
                                <th className="text-left px-4 py-3 font-semibold">School</th>
                                <th className="text-left px-4 py-3 font-semibold">Country</th>
                                <th className="text-right px-4 py-3 font-semibold">Athletes</th>
                                <th className="text-right px-4 py-3 font-semibold">Avg Combined</th>
                                <th className="text-left px-4 py-3 font-semibold">Top Athletes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredSchools.map((s) => (
                                <tr key={s.school_name} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                                    <td className="px-4 py-3 font-bold">#{s.combined_rank}</td>
                                    <td className="px-4 py-3 font-medium">{s.school_name}</td>
                                    <td className="px-4 py-3 text-xs">{s.country}</td>
                                    <td className="px-4 py-3 text-right">{s.athlete_count}</td>
                                    <td className="px-4 py-3 text-right font-bold text-cyan-400">{s.avg_combined_score}</td>
                                    <td className="px-4 py-3 text-xs text-muted-foreground">
                                        {(s.top_athletes || []).slice(0, 3).map(a => a.name).join(', ')}
                                    </td>
                                </tr>
                            ))}
                            {filteredSchools.length === 0 && (
                                <tr><td colSpan={6} className="text-center py-12 text-muted-foreground">No schools match the current filters</td></tr>
                            )}
                        </tbody>
                    </table>
                </Card>
            )}
        </div>
    );
};

const WeightSlider = ({ label, icon, value, onChange, description, testId }) => (
    <div>
        <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
                {icon}
                <span className="text-sm font-medium">{label}</span>
            </div>
            <span className="text-xl font-bold text-cyan-400" data-testid={`${testId}-value`}>{value}%</span>
        </div>
        <Slider
            value={[value]}
            onValueChange={([v]) => onChange(v)}
            min={0}
            max={100}
            step={5}
            data-testid={testId}
        />
        <p className="text-xs text-muted-foreground mt-2">{description}</p>
    </div>
);

export default CombinedRankingsTab;
